import React, { useEffect, useState, useRef, createElement } from 'react';
import { DownloadIcon, PrinterIcon, XIcon } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Button } from '../../components/ui/Button';
import { CreditNote, Invoice, Quotation } from '../../types';
import { IssuerHeader } from './IssuerHeader';
import { calculateInvoicePreTaxLine, calculateInvoiceTotals, formatCurrency, type InvoiceTotals } from './utils';
export type DocumentPreviewAction = 'view' | 'print' | 'download';
type DocumentCustomer = {
  name: string;
  fileNumber?: string;
  address?: string;
  contact?: string;
};
type BillingDocumentPreviewModalProps = {
  record: Quotation | CreditNote;
  documentType: 'Quotation' | 'Credit Note';
  customer: DocumentCustomer;
  relatedInvoice?: Invoice;
  onClose: () => void;
  initialAction?: DocumentPreviewAction;
};
export function BillingDocumentPreviewModal({
  record,
  documentType,
  customer,
  relatedInvoice,
  onClose,
  initialAction = 'view'
}: BillingDocumentPreviewModalProps) {
  const printableRef = useRef<HTMLDivElement>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const isQuotation = documentType === 'Quotation';
  const quotation = isQuotation ? record as Quotation : undefined;
  const creditNote = !isQuotation ? record as CreditNote : undefined;
  const documentNumber = isQuotation ? quotation?.quotationNumber ?? '' : creditNote?.creditNoteNumber ?? '';
  const quotationTotals = quotation ? calculateInvoiceTotals(quotation.items) : undefined;
  const handlePrint = () => {
    document.body.classList.add('printing-test-modal');
    window.print();
    window.setTimeout(() => {
      document.body.classList.remove('printing-test-modal');
    }, 500);
  };
  const handleDownloadPdf = async () => {
    if (!printableRef.current) return;
    setIsGeneratingPdf(true);
    try {
      await captureToA4Pdf(printableRef.current, `BMED_${documentNumber.replace(/[^a-z0-9-_]/gi, '_')}.pdf`);
    } catch (error) {
      console.error('PDF generation failed', error);
      window.alert('Failed to generate the PDF.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  useEffect(() => {
    if (initialAction === 'print') {
      const timer = window.setTimeout(handlePrint, 0);
      return () => window.clearTimeout(timer);
    }
    if (initialAction === 'download') {
      const timer = window.setTimeout(() => void handleDownloadPdf(), 0);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, []);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 print:bg-white print:p-0" role="dialog" aria-modal="true" aria-labelledby="billing-document-preview-title" data-id="element-975">
      <div className="test-modal-print max-h-[95vh] w-full max-w-5xl overflow-y-auto rounded-lg bg-white shadow-2xl print:max-h-none print:max-w-none print:overflow-visible print:rounded-none print:shadow-none" data-id="element-976">
        <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-5 py-3 print:hidden" data-id="element-977">
          <h2 id="billing-document-preview-title" className="text-lg font-semibold text-slate-900" data-id="element-978">
            {documentType} {documentNumber}
          </h2>
          <div className="flex items-center gap-2" data-id="element-979">
            <Button variant="outline" size="sm" onClick={handlePrint} data-id="element-980">
              <PrinterIcon className="mr-1 h-4 w-4" data-id="element-981" /> Print
            </Button>
            <Button size="sm" onClick={handleDownloadPdf} disabled={isGeneratingPdf} data-id="element-982">
              <DownloadIcon className="mr-1 h-4 w-4" data-id="element-983" />
              {isGeneratingPdf ? 'Generating…' : 'Download PDF'}
            </Button>
            <button type="button" onClick={onClose} className="rounded p-1.5 text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500" aria-label={`Close ${documentType.toLowerCase()}`} data-id="element-984">
              <XIcon className="h-5 w-5" data-id="element-985" />
            </button>
          </div>
        </div>

        <article ref={printableRef} className="billing-document bg-white p-0 text-slate-950" data-id="element-986">
          <header className="flex flex-col gap-6 border-b-2 border-slate-900 pb-5 sm:flex-row sm:items-start sm:justify-between" data-id="element-987">
            <IssuerHeader data-id="element-988" />
            <div className="text-left sm:text-right" data-id="element-989">
              <h1 className="text-2xl font-bold tracking-[0.14em] text-slate-800 sm:text-3xl" data-id="element-990">
                {documentType.toUpperCase()}
              </h1>
              <p className="mt-3" data-id="element-991">
                <span className="font-semibold" data-id="element-992">{documentType} #:</span>{' '}
                {documentNumber}
              </p>
              <p data-id="element-993">
                <span className="font-semibold" data-id="element-994">Date:</span>{' '}
                {new Date(record.date).toLocaleDateString()}
              </p>
              {quotation && <p data-id="element-995">
                  <span className="font-semibold" data-id="element-996">Valid until:</span>{' '}
                  {new Date(quotation.validUntil).toLocaleDateString()}
                </p>}
              <p data-id="element-997">
                <span className="font-semibold" data-id="element-998">Status:</span> {record.status}
              </p>
            </div>
          </header>

          <section className="my-7 grid gap-6 border-b border-slate-200 pb-7 sm:grid-cols-2" data-id="element-999">
            <div data-id="element-1000">
              <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-1001">
                {isQuotation ? 'Prepared For' : 'Credit Issued To'}
              </h2>
              <p className="text-lg font-bold text-slate-900" data-id="element-1002">
                {customer.name}
              </p>
              {customer.fileNumber && <p data-id="element-1003">File No: {customer.fileNumber}</p>}
              {customer.address && <p data-id="element-1004">{customer.address}</p>}
              {customer.contact && <p data-id="element-1005">{customer.contact}</p>}
            </div>
            <div className="sm:text-right" data-id="element-1006">
              <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-1007">
                {isQuotation ? 'Quotation Summary' : 'Related Invoice'}
              </h2>
              {quotation ? <>
                  <p data-id="element-1008">
                    <span className="font-semibold" data-id="element-1009">Items:</span>{' '}
                    {quotation.items.length}
                  </p>
                  <p data-id="element-1010">
                    <span className="font-semibold" data-id="element-1011">Estimate:</span>{' '}
                    {quotationTotals ? formatCurrency(quotationTotals.totalAmount) : '—'}
                  </p>
                </> : <>
                  <p data-id="element-1012">
                    <span className="font-semibold" data-id="element-1013">Invoice #:</span>{' '}
                    {relatedInvoice?.invoiceNumber ?? '—'}
                  </p>
                  <p data-id="element-1014">
                    <span className="font-semibold" data-id="element-1015">Original total:</span>{' '}
                    {relatedInvoice ? formatCurrency(relatedInvoice.totalAmount) : '—'}
                  </p>
                </>}
            </div>
          </section>

          {quotation ? <>
              <div className="overflow-x-auto" data-id="element-1016">
                <table className="mb-7 min-w-[720px] w-full border-collapse" data-id="element-1017">
                  <thead data-id="element-1018">
                    <tr className="border-b-2 border-slate-300 bg-slate-100" data-id="element-1019">
                      <th scope="col" className="p-2.5 text-left text-xs font-bold uppercase text-slate-600" data-id="element-1020">
                        Service
                      </th>
                      <th scope="col" className="p-2.5 text-right text-xs font-bold uppercase text-slate-600" data-id="element-1021">
                        Unit Price
                      </th>
                      <th scope="col" className="p-2.5 text-right text-xs font-bold uppercase text-slate-600" data-id="element-1022">
                        Discount
                      </th>
                      <th scope="col" className="p-2.5 text-right text-xs font-bold uppercase text-slate-600" data-id="element-1023">
                        Pre-tax Total
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-1024">
                    {quotation.items.map(item => {
                  const line = calculateInvoicePreTaxLine(item);
                  return <tr key={item.id} className="border-b border-slate-200" data-id="element-1025">
                          <td className="p-2.5" data-id="element-1026">
                            <p className="font-semibold" data-id="element-1027">{item.serviceName}</p>
                            <p className="mt-0.5 text-xs text-slate-500" data-id="element-1028">
                              Qty {item.quantity}
                              {item.description ? ` · ${item.description}` : ''}
                            </p>
                          </td>
                          <td className="p-2.5 text-right" data-id="element-1029">
                            {formatCurrency(item.unitPrice)}
                          </td>
                          <td className="p-2.5 text-right" data-id="element-1030">
                            {item.discount}%
                            <span className="block text-xs text-red-600" data-id="element-1031">
                              -{formatCurrency(line.discountAmount)}
                            </span>
                          </td>
                          <td className="p-2.5 text-right font-semibold" data-id="element-1032">
                            {formatCurrency(line.total)}
                          </td>
                        </tr>;
                })}
                  </tbody>
                </table>
              </div>
              {quotationTotals && <DocumentTotals totals={quotationTotals} totalLabel="Total" data-id="element-1033" />}
              {(quotation.notes || quotation.terms) && <section className="mt-8 grid gap-6 border-t border-slate-200 pt-6 sm:grid-cols-2" data-id="element-1034">
                  {quotation.notes && <DocumentText label="Notes" value={quotation.notes} data-id="element-1035" />}
                  {quotation.terms && <DocumentText label="Terms" value={quotation.terms} data-id="element-1036" />}
                </section>}
            </> : creditNote ? <section className="space-y-6" data-id="element-1037">
              <div className="border-y-2 border-slate-300 py-5" data-id="element-1038">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between" data-id="element-1039">
                  <span className="text-base font-semibold" data-id="element-1040">Credit amount</span>
                  <span className="text-2xl font-bold text-emerald-700" data-id="element-1041">
                    {formatCurrency(creditNote.amount)}
                  </span>
                </div>
              </div>
              <DocumentText label="Reason for credit" value={creditNote.reason} data-id="element-1042" />
              {relatedInvoice && <div className="rounded-md border border-slate-200 bg-slate-50 p-4" data-id="element-1043">
                  <h2 className="mb-3 text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-1044">
                    Invoice financial summary
                  </h2>
                  <div className="grid gap-3 text-sm sm:grid-cols-3" data-id="element-1045">
                    <p data-id="element-1046">
                      <span className="block text-slate-500" data-id="element-1047">
                        Invoice total
                      </span>
                      <span className="font-semibold" data-id="element-1048">
                        {formatCurrency(relatedInvoice.totalAmount)}
                      </span>
                    </p>
                    <p data-id="element-1049">
                      <span className="block text-slate-500" data-id="element-1050">Paid to date</span>
                      <span className="font-semibold" data-id="element-1051">
                        {formatCurrency(relatedInvoice.amountPaid)}
                      </span>
                    </p>
                    <p data-id="element-1052">
                      <span className="block text-slate-500" data-id="element-1053">Balance due</span>
                      <span className="font-semibold" data-id="element-1054">
                        {formatCurrency(relatedInvoice.balanceDue)}
                      </span>
                    </p>
                  </div>
                </div>}
            </section> : null}

          <footer className="mt-12 border-t border-slate-200 pt-5 text-xs text-slate-500" data-id="element-1055">
            This document was generated by BMED Medical Centre billing.
          </footer>
        </article>
      </div>
    </div>;
}
type DocumentTotalsProps = {
  totals: InvoiceTotals;
  totalLabel: string;
};
function DocumentTotals({
  totals,
  totalLabel
}: DocumentTotalsProps) {
  return <dl className="ml-auto w-full max-w-xs" data-id="element-1056">
      <div className="flex justify-between py-1" data-id="element-1057">
        <dt className="text-slate-600" data-id="element-1058">Subtotal:</dt>
        <dd className="font-medium" data-id="element-1059">{formatCurrency(totals.subtotal)}</dd>
      </div>
      {totals.totalDiscount > 0 && <div className="flex justify-between py-1" data-id="element-1060">
          <dt className="text-slate-600" data-id="element-1061">Discount:</dt>
          <dd className="font-medium text-red-600" data-id="element-1062">
            -{formatCurrency(totals.totalDiscount)}
          </dd>
        </div>}
      <div className="flex justify-between border-b border-slate-300 py-1" data-id="element-1063">
        <dt className="text-slate-600" data-id="element-1064">TOT Tax (5%):</dt>
        <dd className="font-medium" data-id="element-1065">{formatCurrency(totals.totalTax)}</dd>
      </div>
      <div className="flex justify-between py-3 text-lg font-bold" data-id="element-1066">
        <dt data-id="element-1067">{totalLabel}:</dt>
        <dd className="text-blue-700" data-id="element-1068">{formatCurrency(totals.totalAmount)}</dd>
      </div>
    </dl>;
}
type DocumentTextProps = {
  label: string;
  value: string;
};
function DocumentText({
  label,
  value
}: DocumentTextProps) {
  return <div data-id="element-1069">
      <h2 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-1070">
        {label}
      </h2>
      <p className="whitespace-pre-wrap text-slate-700" data-id="element-1071">{value}</p>
    </div>;
}
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  const pageWidth = 210;
  const pageHeight = 297;
  const marginTop = 17.78;
  const marginRight = 10.16;
  const marginBottom = 17.78;
  const marginLeft = 12.7;
  const contentWidth = pageWidth - marginLeft - marginRight;
  const contentHeight = pageHeight - marginTop - marginBottom;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  const pixelsPerMillimetre = sourceCanvas.width / contentWidth;
  const sliceHeight = Math.floor(contentHeight * pixelsPerMillimetre);
  let cursor = 0;
  let page = 0;
  while (cursor < sourceCanvas.height) {
    const height = Math.min(sliceHeight, sourceCanvas.height - cursor);
    const slice = document.createElement('canvas');
    slice.width = sourceCanvas.width;
    slice.height = height;
    const context = slice.getContext('2d');
    if (!context) break;
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, slice.width, slice.height);
    context.drawImage(sourceCanvas, 0, cursor, sourceCanvas.width, height, 0, 0, slice.width, height);
    if (page > 0) pdf.addPage();
    pdf.addImage(slice.toDataURL('image/jpeg', 0.95), 'JPEG', marginLeft, marginTop, contentWidth, height / pixelsPerMillimetre);
    cursor += height;
    page += 1;
  }
  pdf.save(filename);
}